<template>
  <form @submit.prevent="formSubmit">
    <h1 class="container-title" :class="[`text-${titleAlignment}`]">
      {{ title ? "" : $t(`global.${company.name}`) }} {{ title ? title : $t(`global.login`) }}
    </h1>
    <FormMessage :errors="errors" v-if="errors" />
    <FormInput
      name="email"
      v-model="loginFields.email"
      testId="email-input"
      :placeholder="$t('global.email')"
      :label="`${$t('global.email')} *`"
      :errors="validation.fields.email.errors"
      :dirty="validation.fields.email.dirty"
    />
    <FormInput
      name="password"
      v-model="loginFields.password"
      type="password"
      testId="password-input"
      :placeholder="$t('global.password')"
      :label="`${$t('global.password')} *`"
      :errors="validation.fields.password.errors"
      :dirty="validation.fields.password.dirty"
    />
    <template v-if="loginFields.twoFactorRequired">
      <div v-if="smsSent" class="text-secondary text-center form-group mt-4">
        {{ $t("global.sms-success-1") }}
        <a href="" target="_blank" @click.prevent="send2faSMS">{{ $t("global.click-here") }}</a>
        {{ $t("global.sms-success-2") }}
      </div>
      <FormInput
        name="two-factor-code"
        v-model="loginFields.twoFactorCode"
        type="password"
        testId="two-factor-code-input"
        :placeholder="$t('global.authentication-code')"
        :label="`${$t('global.authentication-code')} *`"
        :errors="validation.fields.twoFactorCode.errors"
        :dirty="validation.fields.twoFactorCode.dirty"
      />
      <FormCheckbox
        name="remember-me"
        v-model="loginFields.rememberMe"
        testId="remember-me-input"
        :label="`${$t('global.remember-device', { days: 30 })} *`"
        :errors="validation.fields.rememberMe.errors"
        :dirty="validation.fields.rememberMe.dirty"
      />
    </template>
    <div class="form-actions">
      <template v-if="useRecaptcha">
        <template v-if="recaptchaType === 'invisible'">
          <Btn
            v-if="enableBack"
            variant="light"
            class="mr-2"
            type="button"
            :disabled="loading"
            @click="
              () => {
                backAction();
              }
            "
          >
            Back
          </Btn>
          <vue-recaptcha
            ref="recaptchaContainer"
            :sitekey="recaptchaSiteKey"
            :loadRecaptchaScript="true"
            @verify="formSubmit"
            @expired="onExpired"
            class="recaptcha-container"
          >
            <Btn
              variant="primary"
              type="submit"
              :disabled="loading"
              :loading="loading"
              data-test="login-button"
            >
              {{ $t("global.login") }}
            </Btn>
          </vue-recaptcha>
        </template>
        <template v-else>
          <vue-recaptcha
            ref="recaptchaContainer"
            :sitekey="recaptchaSiteKey"
            :loadRecaptchaScript="true"
            @verify="onVerify"
            @expired="onExpired"
            class="recaptcha-container"
          ></vue-recaptcha>
          <Btn
            v-if="enableBack"
            variant="light"
            class="mr-2"
            type="button"
            :disabled="loading"
            @click="
              () => {
                backAction();
              }
            "
          >
            Back
          </Btn>
          <Btn
            variant="primary"
            type="submit"
            :disabled="loading"
            :loading="loading"
            data-test="login-button"
          >
            {{ $t("global.login") }}
          </Btn>
        </template>
      </template>
      <Btn
        v-else
        variant="primary"
        type="submit"
        :disabled="loading"
        :loading="loading"
        @click="submit"
        data-test="login-button"
      >
        {{ $t("global.login") }}
      </Btn>
      <div v-if="loginFields.twoFactorRequired && hasCell && !smsSent">
        <Btn variant="link" @click="send2faSMS">
          {{ $t("global.authenticate-via-sms") }}
        </Btn>
      </div>
      <div class="login-links">
        <div class="forgot my-2">
          <a href="javascript:void(0)" @click="onForgotPasswordClick()"
            >{{ $t("global.forgot-password") }}?</a
          >
        </div>
        <div class="sign-up my-2">
          {{ $t("global.sign-up-intro") }}
          <a href="javascript:void(0)" @click="onSignUpClick()">{{ $t("global.sign-up") }}.</a>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import constants from "@/utils/constants";
import FormCheckbox from "@/components/display/form/FormCheckbox";
import FormInput from "@/components/display/form/FormInput";
import FormMessage from "@/components/display/form/FormMessage";
import { fromServer } from "@/utils/translations";
import { login as loginValidationRules } from "leatherman-js/utils/validations/forms";
import router from "@/router";
import store from "@/store";
import useCompany from "@/utils/useCompany";
import useValidator from "@/utils/useValidator";
import VueRecaptcha from "vue-recaptcha";

export default {
  name: "LoginForm",
  components: {
    Btn,
    FormInput,
    FormCheckbox,
    FormMessage,
    VueRecaptcha,
  },
  props: {
    forgotPasswordAction: {
      type: Function,
      default: () => {
        router.push({ name: constants.FORGOT_PASSWORD_ROUTE });
      },
    },
    signupAction: {
      type: Function,
      default: () => {
        router.push({ name: constants.SIGNUP_ROUTE });
      },
    },
    backAction: Function,
    enableBack: Boolean,
    title: String,
    titleAlignment: {
      type: String,
      default: "center",
    },
  },
  setup(props, { emit, root }) {
    // display variables
    const loading = ref(false);
    const hasCell = ref(true);
    const errors = ref(null);

    // login variables and validation
    const loginFields = reactive({
      email: process.env.VUE_APP_DEFAULT_EMAIL,
      password: process.env.VUE_APP_DEFAULT_PASSWORD,
      twoFactorRequired: false,
      twoFactorCode: null,
      rememberMe: false,
      recaptchaToken: null,
    });
    const { validator } = useValidator();
    const validation = validator(
      loginFields,
      loginValidationRules,
      {},
      {
        email: {
          required: root.$t("validation.email-required"),
        },
        password: {
          required: root.$t("validation.password-required"),
        },
        twoFactorCode: {
          regex: root.$t("validation.invalid-authentication-code"),
          requiredIf: root.$t("validation.authentication-code-required"),
        },
      }
    );

    //recaptcha variables and methods
    const recaptchaType = ref(process.env.VUE_APP_RECAPTCHA_TYPE);
    const recaptchaSiteKey = ref(process.env.VUE_APP_RECAPTCHA_SITE_KEY);
    const recaptchaContainer = ref(null);
    const useRecaptcha = computed(() => {
      return store.state.settings.loginRecaptchaEnabled;
    });

    const formSubmit = (token) => {
      if (recaptchaType.value === "invisible") {
        onVerify(token);
      }

      submit();
    };

    const onVerify = (token) => {
      loginFields.recaptchaToken = token;
    };

    const onExpired = () => {
      resetRecaptcha();
    };

    const onForgotPasswordClick = () => {
      props.forgotPasswordAction();
      emit("close");
    };

    const onSignUpClick = () => {
      props.signupAction();
      emit("close");
    };

    const resetRecaptcha = () => {
      loginFields.recaptchaToken = null;
      if (recaptchaContainer.value) {
        recaptchaContainer.value.reset();
      }
    };
    const { company } = useCompany();

    // submit the form
    const submit = () => {
      errors.value = null;

      validation
        .validate()
        .then(() => {
          loading.value = true;
          store
            .dispatch("auth/login", {
              email: loginFields.email,
              password: loginFields.password,
              recaptchaToken: loginFields.recaptchaToken,
              twoFactorCode: loginFields.twoFactorCode,
              rememberMe: loginFields.rememberMe,
            })
            .then(() => {
              emit("success");
            })
            .catch((e) => {
              resetRecaptcha();
              // if two factor is required, show the two factor code field
              if (e.twoFactorRequired) {
                loginFields.twoFactorRequired = true;
                // if the user has a cell number available for two factor, show the sms button
                hasCell.value = !!e.hasCell;
                validation.reset();
              } else {
                errors.value = fromServer(e.request.response);
              }
            })
            .finally(() => {
              loading.value = false;
            });
        })
        .catch(() => {
          resetRecaptcha();
        });
    };

    const smsLoading = ref(false);
    const smsSent = ref(false);
    const send2faSMS = () => {
      smsLoading.value = true;
      store
        .dispatch("auth/send2faSMS", loginFields.email)
        .then(() => {
          smsSent.value = true;
        })
        .finally(() => (smsLoading.value = false));
    };

    // component is mounted; reset recaptcha
    onMounted(() => {
      resetRecaptcha();
    });

    return {
      errors,
      loginFields,
      loading,
      onExpired,
      onVerify,
      recaptchaSiteKey,
      recaptchaContainer,
      resetRecaptcha,
      submit,
      useRecaptcha,
      recaptchaType,
      validation,
      hasCell,
      company,
      smsLoading,
      smsSent,
      send2faSMS,
      formSubmit,
      onForgotPasswordClick,
      onSignUpClick,
    };
  },
};
</script>

<style lang="scss" scoped>
.login-links {
  font-size: 0.75rem;
  text-align: center;
  .forgot {
    margin-bottom: 1rem;
  }
}
</style>
